import {useCallback, useEffect, useMemo, useState, type ReactNode} from "react";
import {
  Building2, Package, Percent, Save, ShoppingBag, ShoppingCart, Tag, Trash2, User
} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {
  adminClientsExportUrl,
  deleteAdminClient,
  getAdminClient,
  listAdminBrands,
  listAdminClientFirme,
  listAdminClients,
  listAdminOrders,
  updateAdminClient,
  type AdminBrand,
  type AdminClientDetail,
  type AdminClientRow,
  type AdminOrderSummary
} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {useAdminSectionBadges} from "@/hooks/useAdminSectionBadges";
import {ADMIN_SECTION_NAV} from "@/lib/adminSectionNav";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {ClientOrdersPanel} from "./ClientOrdersPanel";
import {
  AdminAlert,
  AdminBadge,
  AdminDualList,
  AdminEmptyState,
  AdminEntityAvatar,
  AdminField,
  AdminFilterBar,
  AdminFilterField,
  AdminFormActions,
  AdminFormCard,
  AdminInviteLink,
  AdminModalKpis,
  AdminModernTable,
  AdminPageLayout,
  AdminPagination,
  AdminRowTrailing,
  AdminSectionTabs,
  AdminTabbedModal,
  AdminToggleField,
  AdminToolbar
} from "./ui";

function tabLabel(icon: ReactNode, text: string) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {icon}
      {text}
    </span>
  );
}

export function ClientsView() {
  const navigate = useNavigate();
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const badges = useAdminSectionBadges("clienti");
  const [items, setItems] = useState<AdminClientRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [comenzi, setComenzi] = useState("");
  const [firma, setFirma] = useState("");
  const [firme, setFirme] = useState<string[]>([]);
  const [brands, setBrands] = useState<AdminBrand[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AdminClientDetail | null>(null);
  const [modalTab, setModalTab] = useState("profil");
  const [clientOrders, setClientOrders] = useState<AdminOrderSummary[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [form, setForm] = useState({
    nume: "", email: "", telefon: "", discount: "", procent: "", firma: "",
    firmaCui: "", firmaJ: "", firmaSediu: "", firmaBanca: "", firmaIban: "",
    branduriBlocate: [] as number[],
    influencer: false
  });
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    listAdminClients({page, pageSize: 20, search: searchQuery || undefined, comenzi: comenzi || undefined, firma: firma || undefined})
      .then((r) => {
        setItems(r.items);
        setTotalPages(r.totalPages);
      })
      .catch((e: Error) => setErr(e.message));
  }, [page, searchQuery, comenzi, firma]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listAdminClientFirme().then((r) => setFirme(r.items)).catch(() => {});
    listAdminBrands().then((r) => setBrands(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if(!detail) {
      setClientOrders([]);
      return;
    }
    setOrdersLoading(true);
    listAdminOrders({clientId: detail.id, page: 1, pageSize: 50})
      .then((r) => setClientOrders(r.items))
      .catch(() => setClientOrders([]))
      .finally(() => setOrdersLoading(false));
  }, [detail]);

  const commitSearch = useCallback(() => {
    setSearchQuery(search);
    setPage(1);
  }, [search]);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) {
      setOpenId(null);
      setDetail(null);
      setModalTab("profil");
      setClientOrders([]);
    }
  }

  async function openItem(id: number) {
    setOpenId(id);
    setModalTab("profil");
    setModalOpen(true);
    const c = await getAdminClient(id);
    setDetail(c);
    setForm({
      nume: c.nume, email: c.email, telefon: c.telefon, discount: c.discount, procent: c.procent,
      firma: c.firma, firmaCui: c.firmaCui, firmaJ: c.firmaJ, firmaSediu: c.firmaSediu,
      firmaBanca: c.firmaBanca, firmaIban: c.firmaIban, branduriBlocate: c.branduriBlocate,
      influencer: Boolean(c.influencer)
    });
  }

  async function saveClient(id: number, andClose = false) {
    try {
      await updateAdminClient(id, form);
      load();
      setDetail(await getAdminClient(id));
      adminToast.success("Client saved.");
      if(andClose) closeModal(false);
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Could not save client");
    }
  }

  function removeClient(id: number) {
    confirm("Delete client account?", async () => {
      await deleteAdminClient(id);
      closeModal(false);
      load();
      adminToast.success("Client deleted.");
    });
  }

  function openClientOrder(orderId: number) {
    closeModal(false);
    navigate(`/vadmin/comenzi?open=${orderId}`);
  }

  const orderStats = useMemo(() => {
    const spent = clientOrders.reduce((s, o) => s + o.total, 0);
    const count = Math.max(detail?.ordersCount ?? 0, clientOrders.length);
    const avg = clientOrders.length ? spent / clientOrders.length : 0;
    const last = [...clientOrders].sort((a, b) => b.date - a.date)[0];
    return {spent, count, avg, lastLabel: last?.dateLabel || "—"};
  }, [clientOrders, detail?.ordersCount]);

  const brandOptions = useMemo(
    () => brands.map((b) => ({id: b.id, label: b.nume})),
    [brands]
  );

  const kpiItems = detail ? [
    {
      id: "orders",
      label: "Orders",
      value: orderStats.count,
      icon: Package,
      onClick: () => setModalTab("comenzi")
    },
    {
      id: "spent",
      label: "Total spent",
      value: `${orderStats.spent.toFixed(0)} Lei`,
      icon: ShoppingBag
    },
    {
      id: "avg",
      label: "Average order",
      value: orderStats.avg ? `${orderStats.avg.toFixed(0)} Lei` : "—"
    },
    {
      id: "cart",
      label: "Cart",
      value: detail.cart.totalQty,
      icon: ShoppingCart,
      onClick: () => setModalTab("cos")
    },
    {
      id: "brands",
      label: "Blocked brands",
      value: form.branduriBlocate.length,
      icon: Tag,
      onClick: () => setModalTab("branduri")
    },
    {
      id: "discount",
      label: "Discount",
      value: form.discount || form.procent || "—",
      icon: Percent
    }
  ] : [];

  const clientTabs = detail ? [
    {
      id: "profil",
      label: tabLabel(<User className="h-3.5 w-3.5" />, "Profile"),
      content: (
        <div className="space-y-4">
          <AdminFormCard title="General" description="Contact details for this client">
            <div className="admin-form-grid admin-form-grid--2">
              <AdminField label="Name">
                <Input value={form.nume} onChange={(e) => setForm({...form, nume: e.target.value})} placeholder="Full name" />
              </AdminField>
              <AdminField label="Email">
                <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="name@company.com" />
              </AdminField>
              <AdminField label="Phone">
                <Input value={form.telefon} onChange={(e) => setForm({...form, telefon: e.target.value})} placeholder="Phone number" />
              </AdminField>
            </div>
          </AdminFormCard>

          <AdminFormCard title="Account" description="Commercial settings and access">
            <div className="admin-form-grid admin-form-grid--2">
              <AdminField label="Discount">
                <Input value={form.discount} onChange={(e) => setForm({...form, discount: e.target.value})} placeholder="Discount" />
              </AdminField>
              <AdminField label="Percent">
                <Input value={form.procent} onChange={(e) => setForm({...form, procent: e.target.value})} placeholder="Percent" />
              </AdminField>
            </div>
            {detail.inviteUrl ? <AdminInviteLink url={detail.inviteUrl} /> : null}
            <AdminToggleField
              title="Influencer module"
              description="Enable influencer module for this client"
              checked={form.influencer}
              onCheckedChange={(v) => setForm({...form, influencer: v})}
            />
          </AdminFormCard>

          <AdminFormCard title="Statistics" description="Snapshot from existing client and order data">
            <div className="admin-stat-list">
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Orders</span>
                <span className="admin-stat-list__value">{orderStats.count}</span>
              </div>
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Total spent</span>
                <span className="admin-stat-list__value">{orderStats.spent.toFixed(2)} Lei</span>
              </div>
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Average order</span>
                <span className="admin-stat-list__value">{orderStats.avg ? `${orderStats.avg.toFixed(2)} Lei` : "—"}</span>
              </div>
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Cart value (qty)</span>
                <span className="admin-stat-list__value">{detail.cart.totalQty}</span>
              </div>
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Registered</span>
                <span className="admin-stat-list__value">{detail.registeredLabel || "—"}</span>
              </div>
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Last order</span>
                <span className="admin-stat-list__value">{orderStats.lastLabel}</span>
              </div>
              <div className="admin-stat-list__row">
                <span className="admin-stat-list__label">Blocked brands</span>
                <span className="admin-stat-list__value">{form.branduriBlocate.length}</span>
              </div>
            </div>
          </AdminFormCard>
        </div>
      )
    },
    {
      id: "comenzi",
      label: tabLabel(
        <Package className="h-3.5 w-3.5" />,
        `Orders${detail.ordersCount ? ` (${detail.ordersCount})` : ""}`
      ),
      content: (
        <ClientOrdersPanel
          clientId={detail.id}
          onOpenOrder={openClientOrder}
          orders={clientOrders}
          loading={ordersLoading}
        />
      )
    },
    {
      id: "firma",
      label: tabLabel(<Building2 className="h-3.5 w-3.5" />, "Company"),
      content: detail.tip === 0 ? (
        <div className="space-y-4">
          <AdminFormCard title="Company information" description="Legal and billing details">
            <div className="admin-form-grid admin-form-grid--2">
              <AdminField label="Company name">
                <Input value={form.firma} onChange={(e) => setForm({...form, firma: e.target.value})} placeholder="Company name" />
              </AdminField>
              <AdminField label="CUI">
                <Input value={form.firmaCui} onChange={(e) => setForm({...form, firmaCui: e.target.value})} placeholder="CUI" />
              </AdminField>
              <AdminField label="Reg. Com.">
                <Input value={form.firmaJ} onChange={(e) => setForm({...form, firmaJ: e.target.value})} placeholder="J…" />
              </AdminField>
              <AdminField label="Headquarters">
                <Input value={form.firmaSediu} onChange={(e) => setForm({...form, firmaSediu: e.target.value})} placeholder="Address / city" />
              </AdminField>
            </div>
          </AdminFormCard>
          <AdminFormCard title="Bank details" description="Payment information on file">
            <div className="admin-form-grid admin-form-grid--2">
              <AdminField label="Bank">
                <Input value={form.firmaBanca} onChange={(e) => setForm({...form, firmaBanca: e.target.value})} placeholder="Bank name" />
              </AdminField>
              <AdminField label="IBAN">
                <Input value={form.firmaIban} onChange={(e) => setForm({...form, firmaIban: e.target.value})} placeholder="IBAN" />
              </AdminField>
            </div>
          </AdminFormCard>
        </div>
      ) : (
        <AdminEmptyState
          icon={Building2}
          title="Not a company account"
          description="Company fields are available only for company-type clients."
        />
      )
    },
    {
      id: "cos",
      label: tabLabel(<ShoppingCart className="h-3.5 w-3.5" />, "Cart"),
      content: (
        <div>
          <div className="admin-cart-summary">
            <div className="admin-cart-summary__card">
              <p className="admin-cart-summary__label">Products</p>
              <p className="admin-cart-summary__value">{detail.cart.totalQty}</p>
            </div>
            <div className="admin-cart-summary__card">
              <p className="admin-cart-summary__label">Updated</p>
              <p className="admin-cart-summary__value" style={{fontSize: "0.95rem"}}>
                {detail.cart.updatedAt || "—"}
              </p>
            </div>
          </div>
          {!detail.cart.items.length ? (
            <AdminEmptyState
              icon={ShoppingCart}
              title="Cart is empty"
              description="This client has no products in their cart."
            />
          ) : (
            <div className="admin-cart-list">
              {detail.cart.items.map((i) => (
                <div key={`${i.productId}-${i.location}`} className="admin-cart-row">
                  <div className="admin-cart-row__top">
                    <div>
                      <p className="admin-cart-row__title">{i.name}</p>
                      <p className="admin-cart-row__sku">SKU {i.code} · ID {i.productId}</p>
                    </div>
                    <div className="admin-cart-row__badges">
                      <AdminBadge variant="secondary">{i.location || "Warehouse"}</AdminBadge>
                      <span className="admin-cart-row__qty">×{i.quantity}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    },
    {
      id: "branduri",
      label: tabLabel(<Tag className="h-3.5 w-3.5" />, "Brands"),
      content: (
        <AdminFormCard
          title="Blocked brands"
          description="Move brands to the blocked list to hide them from this client"
        >
          <AdminDualList
            options={brandOptions}
            selectedIds={form.branduriBlocate}
            onChange={(ids) => setForm({...form, branduriBlocate: ids})}
            availableTitle="Available brands"
            selectedTitle="Blocked brands"
            searchPlaceholder="Search brands…"
          />
        </AdminFormCard>
      )
    }
  ] : [];

  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;

  return (
    <AdminPageLayout
      title={ADMIN_SECTION_NAV.clienti.title}
      description="Manage client accounts"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Clients"}]}
    >
      {confirmDialog}
      <AdminSectionTabs section="clienti" badges={badges} />
      <AdminToolbar>
        <Button variant="secondary" size="sm" asChild>
          <a href={adminClientsExportUrl()} target="_blank" rel="noreferrer">Export clients</a>
        </Button>
      </AdminToolbar>
      <AdminFilterBar
        onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }}
        onReset={() => { setComenzi(""); setFirma(""); setSearch(""); setSearchQuery(""); setPage(1); }}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, email, phone, company..."
        itemCount={items.length}
      >
        <AdminFilterField label="Orders">
          <Select value={comenzi || "all"} onValueChange={(v) => setComenzi(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All orders</SelectItem>
              <SelectItem value="1">No orders</SelectItem>
              <SelectItem value="2">At least one order</SelectItem>
              <SelectItem value="3">Has items in cart</SelectItem>
            </SelectContent>
          </Select>
        </AdminFilterField>
        <AdminFilterField label="Company">
          <Select value={firma || "all"} onValueChange={(v) => setFirma(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {firme.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </AdminFilterField>
      </AdminFilterBar>
      <AdminModernTable
        items={items}
        getId={(c) => c.id}
        getTitle={(c) => c.nume}
        getSubtitle={(c) => c.email}
        getMeta={(c) => (
          <>
            {c.firma ? <AdminBadge variant="secondary">{c.firma}</AdminBadge> : null}
            {c.influencer ? <AdminBadge>Influencer</AdminBadge> : null}
            {(c.cartQty ?? 0) > 0 ? <AdminBadge variant="secondary"><ShoppingCart className="h-3 w-3" /> {c.cartQty}</AdminBadge> : null}
            <AdminBadge variant="outline">{c.registeredLabel}</AdminBadge>
          </>
        )}
        getSearchText={(c) => adminSearchText(c.id, c.nume, c.email, c.telefon, c.firma, c.discount, c.procent, c.cod, c.registeredLabel, c.cartQty)}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={commitSearch}
        clientFilter={false}
        hideSearch
        selectedId={openId}
        onRowClick={(c) => openItem(c.id)}
        emptyTitle="No clients"
        emptyDescription="Adjust filters or check back later."
        searchPlaceholder="Search name, email, phone, company..."
        getRowActions={(c) => (
          <AdminRowTrailing onDelete={() => removeClient(c.id)} deleteLabel="Delete client" />
        )}
      />
      {detail && typeof openId === "number" ? (
        <AdminTabbedModal
          open={modalOpen}
          onOpenChange={closeModal}
          size="full"
          title={(
            <div className="admin-entity-header">
              <div className="admin-entity-header__main">
                <AdminEntityAvatar name={detail.nume} />
                <div className="admin-entity-header__copy">
                  <div className="admin-entity-header__name">{detail.nume}</div>
                  <div className="admin-entity-header__email">{detail.email}</div>
                  <div className="admin-entity-header__badges">
                    <span className="admin-modal__meta-chip">Client #{detail.id}</span>
                    <span className="admin-modal__meta-chip admin-modal__meta-chip--accent">
                      {detail.ordersCount} orders
                    </span>
                    {detail.influencer ? <span className="admin-modal__meta-chip">Influencer</span> : null}
                    {detail.tip === 0 ? (
                      <span className="admin-modal__meta-chip admin-modal__meta-chip--muted">Company</span>
                    ) : (
                      <span className="admin-modal__meta-chip admin-modal__meta-chip--muted">Employee</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="admin-entity-header__side">
                <div>
                  <span className="admin-entity-header__stat-label">Registered</span>
                  <span className="admin-entity-header__stat-value">{detail.registeredLabel || "—"}</span>
                </div>
                <div>
                  <span className="admin-entity-header__stat-label">Last order</span>
                  <span className="admin-entity-header__stat-value">{orderStats.lastLabel}</span>
                </div>
                <div>
                  <span className="admin-entity-header__stat-label">Cart</span>
                  <span className="admin-entity-header__stat-value">{detail.cart.totalQty} items</span>
                </div>
                <div>
                  <span className="admin-entity-header__stat-label">Invite</span>
                  <span className="admin-entity-header__stat-value">{detail.cod || "—"}</span>
                </div>
              </div>
            </div>
          )}
          description={<span className="sr-only">Edit client {detail.nume}</span>}
          headerExtra={<AdminModalKpis items={kpiItems} />}
          tabs={clientTabs}
          tab={modalTab}
          onTabChange={setModalTab}
          footer={(
            <AdminFormActions
              left={(
                <Button type="button" variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => removeClient(openId)}>
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              )}
            >
              <Button type="button" variant="outline" size="sm" onClick={() => closeModal(false)}>Cancel</Button>
              <Button type="button" variant="secondary" size="sm" onClick={() => saveClient(openId, true)}>
                Save & Close
              </Button>
              <Button type="button" size="sm" onClick={() => saveClient(openId)}>
                <Save className="h-4 w-4" /> Save
              </Button>
            </AdminFormActions>
          )}
        />
      ) : null}
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
